import { useEffect, useRef } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useIntercom } from 'react-use-intercom'
import { useAuth } from 'lib/auth'
import { getAssumedUser } from 'lib/auth/impersonation'
import { getCurrentIntercomBootOptions } from './intercomLauncherVisibility'
import { isIntercomSessionTerminated } from './intercomSessionState'

export const INTERCOM_USER_JWT_QUERY = gql`
  query IntercomUserJwt {
    currentUser {
      intercomUserJwt(platform: WEB)
    }
  }
`

export const resolveIntercomSession = ({
  isAssumeUserActive,
  isLoggedIn,
  intercomUserJwt,
  sessionTerminated = false,
}) => {
  if (isAssumeUserActive || sessionTerminated) return { kind: 'off' }
  if (isLoggedIn && intercomUserJwt) {
    return { kind: 'verified', jwt: intercomUserJwt }
  }
  return { kind: 'anonymous' }
}

const IntercomSessionSynchronizer = () => {
  const { boot, shutdown, hardShutdown } = useIntercom()
  const { isLoggedIn } = useAuth()
  const isAssumeUserActive = !!getAssumedUser()
  const sessionTerminated = isIntercomSessionTerminated()
  const appliedSessionRef = useRef(null)

  const { data } = useQuery(INTERCOM_USER_JWT_QUERY, {
    skip: !isLoggedIn || isAssumeUserActive || sessionTerminated,
    errorPolicy: 'ignore',
    fetchPolicy: 'network-only',
  })

  const intercomUserJwt = data?.currentUser?.intercomUserJwt ?? null

  useEffect(() => {
    const { kind, jwt } = resolveIntercomSession({
      isAssumeUserActive,
      isLoggedIn,
      intercomUserJwt,
      sessionTerminated,
    })

    const previousSession = appliedSessionRef.current
    if (
      previousSession &&
      previousSession.kind === kind &&
      (kind !== 'verified' || previousSession.jwt === jwt)
    ) {
      return
    }

    if (kind === 'off') {
      hardShutdown()
    } else {
      if (previousSession && previousSession.kind !== 'off') {
        shutdown()
      }
      if (kind === 'verified') {
        boot({
          ...getCurrentIntercomBootOptions(),
          intercomUserJwt: jwt,
        })
      } else {
        boot(getCurrentIntercomBootOptions())
      }
    }

    appliedSessionRef.current = { kind, jwt }
  }, [
    isLoggedIn,
    isAssumeUserActive,
    sessionTerminated,
    intercomUserJwt,
    boot,
    shutdown,
    hardShutdown,
  ])

  return null
}

export default IntercomSessionSynchronizer
